import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useGetAiUserSettingsQuery,
  useVerifyUserApiKeysMutation
} from '@/app/Settings/api/aiSettingsApi';
import type {
  ProviderKey,
  VerifyUserApiKeysData
} from '@/app/Settings/types/aiSettingsApiTypes';
import { useGetWalletBalanceQuery } from '@/app/Wallet/api/walletApi';
import { isUsableWalletBalance } from '@/app/Wallet/utils/walletBalance';
import useDomain from '@/hooks/useDomain';
import mcpApi, {
  useGetMcpAutoBudgetQuery,
  useGetMcpChatOnboardingQuery,
  usePatchMcpChatOnboardingMutation
} from '../api/mcpApi';
import {
  MCP_CHAT_ONBOARDING_STEPS,
  type McpChatOnboardingStepId,
  clearLegacyMcpChatOnboardingLocal,
  readLegacyMcpChatOnboardingLocal
} from '../lib/mcp-chat-onboarding';
import { startNewMcpChat } from '../utils/navigation';

export function useMcpChatOnboardingGate() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { domain_slug } = useDomain();
  const migratedLegacyRef = useRef(false);

  const {
    data: onboarding,
    isLoading: isLoadingOnboarding,
    refetch: refetchOnboarding
  } = useGetMcpChatOnboardingQuery(undefined, {
    refetchOnMountOrArgChange: true
  });

  const [patchOnboarding] = usePatchMcpChatOnboardingMutation();
  const [verifyUserApiKeys, { isLoading: isVerifyingKeys }] =
    useVerifyUserApiKeysMutation();
  const [verifyResult, setVerifyResult] = useState<VerifyUserApiKeysData | null>(
    null
  );

  const {
    data: aiSettings,
    isLoading: isLoadingAiSettings,
    refetch: refetchAiSettings
  } = useGetAiUserSettingsQuery({}, { refetchOnMountOrArgChange: true });

  const {
    data: walletBalance,
    isLoading: isLoadingWallet,
    refetch: refetchWallet
  } = useGetWalletBalanceQuery(
    { domain_slug: domain_slug ?? '' },
    {
      skip: !domain_slug,
      refetchOnMountOrArgChange: true
    }
  );

  // Tells whether the user owns the selected domain.
  const { data: autoBudget, isLoading: isLoadingAutoBudget } =
    useGetMcpAutoBudgetQuery(domain_slug ?? '', {
      skip: !domain_slug,
      refetchOnMountOrArgChange: true
    });

  useEffect(() => {
    if (!onboarding || migratedLegacyRef.current) return;

    const legacy = readLegacyMcpChatOnboardingLocal();
    if (!legacy.consentAccepted && !legacy.entered) {
      migratedLegacyRef.current = true;
      return;
    }

    const needsConsent =
      (legacy.consentAccepted || legacy.entered) && !onboarding.consentAccepted;
    const needsComplete = legacy.entered && !onboarding.completed;

    if (!needsConsent && !needsComplete) {
      clearLegacyMcpChatOnboardingLocal();
      migratedLegacyRef.current = true;
      return;
    }

    migratedLegacyRef.current = true;
    void patchOnboarding({
      ...(needsConsent ? { acceptConsent: true } : {}),
      ...(needsComplete ? { complete: true } : {})
    })
      .unwrap()
      .finally(() => {
        clearLegacyMcpChatOnboardingLocal();
      });
  }, [onboarding, patchOnboarding]);

  const providerKeys = aiSettings?.provider_keys ?? [];
  const activeProviderKeys = useMemo(
    () => providerKeys.filter((key: ProviderKey) => key.is_active),
    [providerKeys]
  );
  const hasActiveApiKey = activeProviderKeys.length > 0;
  const hasVerifiedApiKey = activeProviderKeys.some(
    (key: ProviderKey) => Boolean(key.verified)
  );
  const enableUserAi = Boolean(aiSettings?.settings?.enable_user_ai);
  const hasApiKeyReady = hasActiveApiKey && enableUserAi && hasVerifiedApiKey;

  const aiBalance = walletBalance?.aiBalance ?? 0;
  const hasAiWalletBalance = isUsableWalletBalance(aiBalance);

  const consentAccepted = Boolean(onboarding?.consentAccepted);
  /** Once the user has entered chat once, never show the wizard again. */
  const chatEntered = Boolean(onboarding?.completed);
  const lastSeenStep = (onboarding?.lastSeenStep ?? 1) as McpChatOnboardingStepId;

  const stepStatus = useMemo(
    () =>
      ({
        1: consentAccepted,
        2: hasApiKeyReady,
        3: hasAiWalletBalance
      }) as Record<McpChatOnboardingStepId, boolean>,
    [consentAccepted, hasApiKeyReady, hasAiWalletBalance]
  );

  /**
   * Members of someone else's domain use the owner's budget, keys and wallet,
   * so they skip the wizard. Unknown ownership keeps the wizard.
   */
  const isDomainMember = autoBudget?.isDomainOwner === false;

  // Low AI balance must not reopen onboarding after the first successful entry.
  const isReady = chatEntered || isDomainMember;

  const currentStep: McpChatOnboardingStepId = !stepStatus[1]
    ? 1
    : !stepStatus[2]
      ? 2
      : 3;

  const isLoading =
    isLoadingOnboarding ||
    isLoadingAiSettings ||
    (!!domain_slug && (isLoadingWallet || isLoadingAutoBudget));

  const acceptConsent = useCallback(async () => {
    await patchOnboarding({ acceptConsent: true, lastSeenStep: 2 }).unwrap();
  }, [patchOnboarding]);

  const markSeenStep = useCallback(
    (step: McpChatOnboardingStepId) => {
      if (!onboarding) return;
      if (step <= (onboarding.lastSeenStep ?? 1)) return;
      void patchOnboarding({ lastSeenStep: step });
    },
    [onboarding, patchOnboarding]
  );

  const enterChat = useCallback(
    async (options?: { navigateToChat?: boolean }) => {
      await patchOnboarding({ complete: true, lastSeenStep: 3 }).unwrap();
      // Step 2 already wrote verifiedApiKeysCount. The ring fetched before that.
      dispatch(mcpApi.util.invalidateTags(['McpAutoBudget']));
      if (options?.navigateToChat ?? true) {
        startNewMcpChat(navigate);
      }
    },
    [dispatch, navigate, patchOnboarding]
  );

  const refresh = useCallback(async () => {
    await Promise.all([
      refetchOnboarding(),
      refetchAiSettings(),
      refetchWallet()
    ]);
  }, [refetchAiSettings, refetchOnboarding, refetchWallet]);

  const verifyAndRefresh = useCallback(async () => {
    try {
      const data = await verifyUserApiKeys().unwrap();
      setVerifyResult(data);
    } catch {
      setVerifyResult(null);
    }
    await refresh();
  }, [refresh, verifyUserApiKeys]);

  return {
    steps: MCP_CHAT_ONBOARDING_STEPS,
    currentStep,
    lastSeenStep,
    stepStatus,
    isReady,
    isLoading,
    consentAccepted,
    acceptConsent,
    markSeenStep,
    enterChat,
    hasActiveApiKey,
    hasVerifiedApiKey,
    enableUserAi,
    hasApiKeyReady,
    activeProviderKeys,
    verifyResult,
    isVerifyingKeys,
    verifyAndRefresh,
    aiBalance,
    hasAiWalletBalance,
    domain_slug,
    refresh
  };
}
